import { useRequest } from 'ahooks';
import { isEmpty } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dialog, Loading, MessagePlugin } from 'tdesign-react';

import { Star3 } from '@/components/icon';
import { AIGC_MUSIC_PROMPT_LENGTH, AiMusicReq, AiType, GetAIResourceRsp, ResourceHistory, Status, UrlInfo, UserLevel } from '@/interface/aigc';
import { aiMusic, getAIResource } from '@/services/aigc';
import { CgiErrorCode, CgiResp } from '@/services/cgi';
import { getUnicodeLength } from '@/utils/getUnicodeLength';

import { getUsageExceedMessage } from '../AigcImageComponent/util';
import { AigcMusicDialogProps } from '../AigcMusicDialog';
import AigcMusicHistory from './history';
import styles from './index.module.less';
import AigcMusicInfo, { AigcMusicInfoRef } from './info';

interface AigcMusicProps {
  visible: boolean;
  data: AigcMusicDialogProps['data'];
  getSrc: (src: UrlInfo) => void;
  getData: (data: AiMusicReq) => void;
  pagination: {
    page: number;
    size: number;
    total: number;
  };
  onPageChange: (pageInfo: {
    page: number,
    size: number,
    total: number,
  }) => void;
}

const FAILED_MESSAGE = 'AI生成音乐失败，请点击重试';
const TIMEOUT = 60000 * 6;
const AIGC_MUSIC_COUNT = 3;

const AigcMusicComponent: React.FC<AigcMusicProps> = (props) => {
  const { visible, data, pagination, onPageChange, getSrc, getData } = props;
  const [histories, setHistories] = useState<ResourceHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<UserLevel>();

  const delayTimerRef = useRef<NodeJS.Timeout | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const aigcMusicInfoRef = useRef<AigcMusicInfoRef>(null);

  useEffect(() => {
    if (visible) {
      setLoading(true);
      getMusic();
    } else {
      cleanup({ cleanHistories: false });
    }

    return () => cleanup({ cleanHistories: false });
  }, [visible, pagination.page, pagination.size]);

  const generate = async () => {
    aigcMusicInfoRef.current?.submitForm();
    const musicInfo = aigcMusicInfoRef.current?.getMusicInfo();
    const { prompt = '', tagChoose } = musicInfo || {};
    const { genre = [], mood = [], theme = [] } = tagChoose || {};
    let errorMessage = null;

    if (prompt?.trim().length === 0) {
      errorMessage = '请填写音乐描述';
    } else if (getUnicodeLength(prompt) > AIGC_MUSIC_PROMPT_LENGTH) {
      errorMessage = `最多输入${AIGC_MUSIC_PROMPT_LENGTH}字`;
    }
    if (errorMessage) {
      MessagePlugin.error({ content: errorMessage });
      return;
    }

    const postData: AiMusicReq = {
      prompt,
      queryId: '',
      genre,
      mood,
      theme,
    };

    await generateMusic(postData);
  };

  const generateMusic = async (postData: AiMusicReq) => {
    onPageChange({ ...pagination, page: 1 });

    const { code, data, message } = await aiMusic(postData);
    if (code !== CgiErrorCode.NO_ERROR) {
      MessagePlugin.error({
        content: message || FAILED_MESSAGE,
      });
      if (code === CgiErrorCode.BlackUser) {
        setUser(UserLevel.BLACK);
      } else if (code === CgiErrorCode.UsageExceed) {
        setUser(data?.userLevelType);
      }
      cleanup({ cancelPoll: false, cleanHistories: false });
      return;
    }

    setHistories(pre => [
      {
        urls: Array(AIGC_MUSIC_COUNT).fill({ url: 'initUrl', name: '' }),
        prompt: postData.prompt || '',
        status: Status.WAITING,
        relateId: data?.relateId,
      },
      ...pre,
    ]);

    // 开始轮询
    if (delayTimerRef.current) {
      clearTimeout(delayTimerRef.current);
      delayTimerRef.current = null;
    }
    delayTimerRef.current = setTimeout(() => {
      getMusic();
    }, 1000);
  };

  const getMusic = () => {
    startPolling();
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    timerRef.current = setTimeout(() => {
      MessagePlugin.error('生成超时，请点击重试');
      cleanup();
    }, TIMEOUT);
  };

  const { data: musicRes, run: startPolling, cancel: cancelPolling } = useRequest(
    () => getAIResource({
      queryId: '',
      type: AiType.MUSIC,
      page: pagination.page,
      size: pagination.size,
    }),
    {
      pollingInterval: 5000,
      manual: true,
      pollingWhenHidden: false,
    },
  );

  useEffect(() => {
    if (musicRes) setLoading(false);
    const { code, data } = musicRes as CgiResp<GetAIResourceRsp> || { code: 0, data: {} };
    if (code !== CgiErrorCode.NO_ERROR) {
      MessagePlugin.error(FAILED_MESSAGE);
      cleanup();
      return;
    }
    if (isEmpty(data) || isEmpty(data.data)) {
      return;
    }

    onPageChange({ ...pagination, total: data.totalCnt || 0 });
    setHistories(data.data);
    const isFinished = data.data.every(e => e.status === Status.FINISHED);
    if (isFinished) {
      cleanup();
    }
  }, [musicRes, cancelPolling]);

  const cleanup = (params?: {
    cancelPoll?: boolean,
    cleanHistories?: boolean,
  }) => {
    const { cancelPoll = true, cleanHistories = true } = params || {};
    if (delayTimerRef.current && cancelPoll) {
      clearTimeout(delayTimerRef.current);
      delayTimerRef.current = null;
    }
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (cancelPoll) {
      cancelPolling();
    }

    if (cleanHistories) {
      setHistories(pre => pre.map(history => ({
        ...history,
        status: Status.FINISHED,
      })));
    };
  };

  const handleDelete = (id: string) => {
    const newHistories = histories.filter(e => e.relateId !== id);
    setHistories(newHistories);
  };

  const errMsg = useMemo(() => getUsageExceedMessage(AiType.MUSIC, user), [user]);

  return (
    <div className={styles.aigcMusicComponent}>
      <div className={styles.left}>
        <div className={styles.controlPanel}>
          <AigcMusicInfo
            ref={aigcMusicInfoRef}
            data={data}
            getData={getData}
          />
        </div>
        <div className={styles.generateButtonContainer}>
          <Button
            onClick={generate}
            className={styles.geneBtn}
            theme='default'
          >
            <div className={styles.btnText}>
              <Star3 />生成音乐
            </div>
          </Button>
        </div>
      </div>
      <div className={styles.musicDisplay}>
        <Loading
          loading={loading}
          text='加载中...'
          style={{ display: 'flex', flex: 1 }}
        >
          <AigcMusicHistory
            histories={histories}
            generateMusic={generateMusic}
            getSrc={getSrc}
            onDelete={handleDelete}
            pagination={pagination}
            loading={loading}
          />
        </Loading>
      </div>

      {errMsg && <Dialog
        theme='danger'
        visible={user !== undefined}
        width={400}
        placement='center'
        cancelBtn={false}
        closeBtn={false}
        onConfirm={() => setUser(undefined)}
        header={errMsg.title}
      >
        <div style={{ paddingTop: 10 }}>{errMsg.desc}</div>
      </Dialog>}
    </div>
  );
};

export default AigcMusicComponent;
